import { forwardRef, useId } from "react";
import { cn } from "../lib/cn";
import { Field, useFieldControlProps } from "./Field";

export interface DatePickerProps {
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  label?: string;
  disabled?: boolean;
  invalid?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  "aria-label"?: string;
}

interface NativeDateFieldProps extends DatePickerProps {
  type: "date" | "datetime-local";
  formatValue: (date: Date) => string;
  parseValue: (value: string) => Date;
}

const frameClasses =
  "box-border flex h-10 min-w-0 w-full max-w-full items-center rounded-lg border border-border-control bg-surface-default px-3 transition-colors focus-within:border-border-focused focus-within:ring-2 focus-within:ring-border-focused-subtle";

// iOS 26 WebKit adds temporal-input padding outside width: 100%.
const inputClasses =
  "h-full min-w-0 w-full max-w-full border-0 bg-transparent type-body-lg text-content-primary outline-none disabled:cursor-not-allowed";

const pad = (value: number): string => String(value).padStart(2, "0");

export function formatDateValue(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function formatDateTimeValue(date: Date): string {
  return `${formatDateValue(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function parseDateValue(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function parseDateTimeValue(value: string): Date {
  const [date, time] = value.split("T");
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

export const NativeDateField = forwardRef<
  HTMLInputElement,
  NativeDateFieldProps
>(
  (
    {
      type,
      formatValue,
      parseValue,
      value,
      onChange,
      label,
      disabled = false,
      invalid = false,
      minDate,
      maxDate,
      className,
      "aria-label": ariaLabel,
    },
    ref,
  ) => {
    const generatedId = useId();
    const fieldControlProps = useFieldControlProps({
      id: label ? generatedId : undefined,
      invalid,
    });
    const inputId = fieldControlProps.id ?? generatedId;
    const input = (
      <div
        data-slot="date-picker"
        className={cn(
          frameClasses,
          invalid && "border-surface-error focus-within:border-surface-error",
          disabled && "opacity-38",
        )}
      >
        <input
          ref={ref}
          data-slot="date-picker-input"
          {...fieldControlProps}
          id={inputId}
          type={type}
          value={value ? formatValue(value) : ""}
          min={minDate ? formatValue(minDate) : undefined}
          max={maxDate ? formatValue(maxDate) : undefined}
          disabled={disabled}
          aria-label={ariaLabel}
          className={inputClasses}
          onChange={(event) => {
            onChange?.(
              event.target.value ? parseValue(event.target.value) : null,
            );
          }}
        />
      </div>
    );

    return label ? (
      <Field label={label} htmlFor={inputId} className={className}>
        {input}
      </Field>
    ) : (
      <div className={cn("w-full", className)}>{input}</div>
    );
  },
);
NativeDateField.displayName = "NativeDateField";

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (props, ref) => (
    <NativeDateField
      ref={ref}
      type="date"
      formatValue={formatDateValue}
      parseValue={parseDateValue}
      {...props}
    />
  ),
);
DatePicker.displayName = "DatePicker";
